import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { endOfDay } from 'date-fns';
import {
  AlertTriangle,
  ClipboardList,
  Clock3,
  Download,
  FileText,
  Fingerprint,
  ListRestart,
  Loader2,
  PlayCircle,
  RefreshCw,
  ScrollText,
  Sparkles,
  Trash2,
  Upload
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { useToast } from '../hooks/use-toast';
import { useFeatures } from '../contexts/FeaturesContext';
import useTimeZone from '../hooks/useTimeZone';
import {
  createAnalyticalTasks,
  createClient,
  createEmployee,
  bulkRestartTasks,
  deleteTask,
  downloadTasksArtifacts,
  fetchClients,
  fetchEmployees,
  fetchTaskAnalysis,
  fetchTaskStatus,
  fetchTasks,
  fetchTasksSummary,
  fetchTaskTranscript,
  getTaskAudioUrl,
  restartTask
} from '../api';
import { taskAudioFileName } from '../api/taskAudioUrl';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  BIOMETRY_RESTART_SCOPE,
  canRetryBiometry,
  describeBulkRestart,
  describeBulkRestartResult,
  describeTaskBiometry
} from './tasks/taskBiometry';
import { describeTaskAutoRetry } from './tasks/taskAutoRetry';
import {
  TERMINAL_TASK_STATUSES,
  applyStatusUpdates,
  runTaskStatusPoll,
  selectActiveTasks
} from './tasks/taskStatusPoll';
import {
  matchEmployeeByName,
  normalizePhoneValue,
  parseCallMetaFromFilename
} from '../utils/callFileMetadata';

const PAGE_SIZE = 10;
const STATUS_POLL_INTERVAL_MS = 5000;
const STATUS_FILTER_MAP = {
  all: [],
  active: ['NotStarted', 'RecognitionRunning', 'RecognitionResultsSaved', 'AnalysisCompleted'],
  failed: ['Failed'],
  completed: ['Completed'],
  skipped: ['Skipped']
};
const statusEnumMap = {
  0: 'NotStarted',
  1: 'RecognitionRunning',
  2: 'RecognitionResultsSaved',
  3: 'AnalysisCompleted',
  4: 'Completed',
  5: 'Failed',
  6: 'Skipped'
};

const statusConfig = {
  NotStarted: {
    label: 'В очереди',
    badgeClass: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200',
    dotClass: 'bg-slate-500',
    description: 'Файл ожидает запуска конвейера'
  },
  RecognitionRunning: {
    label: 'Расшифровка',
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    dotClass: 'bg-blue-500',
    description: 'Идет распознавание речи'
  },
  RecognitionResultsSaved: {
    label: 'Транскрипт сохранен',
    badgeClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
    dotClass: 'bg-indigo-500',
    description: 'Результат распознавания записан'
  },
  AnalysisCompleted: {
    label: 'Анализ завершен',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
    dotClass: 'bg-amber-500',
    description: 'Идет запись аналитики'
  },
  Completed: {
    label: 'Готово',
    badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    dotClass: 'bg-emerald-500',
    description: 'Все результаты сохранены'
  },
  Failed: {
    label: 'Ошибка',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    dotClass: 'bg-red-500',
    description: 'Обработка остановлена из-за ошибки'
  },
  Skipped: {
    label: 'Пропущено',
    badgeClass: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
    dotClass: 'bg-zinc-400',
    description: 'Анализ выключен для сотрудника в настройках'
  },
  default: {
    label: 'Неизвестно',
    badgeClass: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
    dotClass: 'bg-gray-500',
    description: 'Статус не распознан'
  }
};

const BIOMETRY_TONE_CLASSES = {
  ok: 'text-emerald-700 dark:text-emerald-400',
  warning: 'text-amber-700 dark:text-amber-400',
  muted: 'text-muted-foreground'
};

const normalizeTaskStatus = (status) => {
  if (typeof status === 'string') {
    return status;
  }

  if (typeof status === 'number') {
    return statusEnumMap[status] ?? 'Unknown';
  }

  return 'Unknown';
};

const mapTaskStatuses = (tasks) =>
  tasks.map((task) => ({
    ...task,
    status: normalizeTaskStatus(task.status)
  }));

const getStatusConfig = (status) => {
  if (status && statusConfig[status]) {
    return statusConfig[status];
  }
  return statusConfig.default;
};

const directionOptions = [
  { value: 'incoming', label: 'Входящий' },
  { value: 'outgoing', label: 'Исходящий' }
];

const directionLabels = directionOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const transcriptReadyStatuses = new Set([
  'RecognitionResultsSaved',
  'AnalysisCompleted',
  'Completed',
  'Failed'
]);

const analysisReadyStatuses = new Set(['Completed']);

const isTranscriptAvailable = (task) => transcriptReadyStatuses.has(task.status);

const isAnalysisAvailable = (task) =>
  analysisReadyStatuses.has(task.status) || Boolean(task.resultsPath);
const isTaskTerminal = (task) => Boolean(task?.status && TERMINAL_TASK_STATUSES.has(task.status));

const createEmptyTaskTableFilters = () => ({
  id: '',
  status: '',
  createdAt: '',
  updatedAt: '',
  source: '',
  metadata: '',
  results: '',
  analysisContent: ''
});

const applyTaskTableFiltersToParams = (params, filters) => {
  if (!filters) {
    return;
  }

  const fieldToQueryMap = {
    id: 'filterId',
    status: 'filterStatus',
    createdAt: 'filterCreatedAt',
    updatedAt: 'filterUpdatedAt',
    source: 'filterSource',
    metadata: 'filterMetadata',
    results: 'filterResults',
    analysisContent: 'filterAnalysisContent'
  };

  Object.entries(fieldToQueryMap).forEach(([field, queryName]) => {
    const value = String(filters[field] ?? '').trim();
    if (value) {
      params[queryName] = value;
    }
  });
};

const buildTaskDateRangeParams = (dateRange) => {
  const params = {};

  if (dateRange?.from) {
    const fromDate = new Date(dateRange.from);
    if (!Number.isNaN(fromDate.getTime())) {
      params.from = fromDate.toISOString();
    }
  }

  if (dateRange?.to) {
    const toDate = endOfDay(new Date(dateRange.to));
    if (!Number.isNaN(toDate.getTime())) {
      params.to = toDate.toISOString();
    }
  }

  return params;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) {
    return null;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes === 0) {
    return `${remainder} сек`;
  }
  if (remainder === 0) {
    return `${minutes} мин`;
  }
  return `${minutes} мин ${remainder} сек`;
};

const rublesFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatProcessedMinutes = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '0,00 мин';
  }

  return `${minutes.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} мин`;
};

const formatEstimatedCost = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    return rublesFormatter.format(0);
  }

  return rublesFormatter.format(amount);
};

const formatLocalDateTimeInput = (value) => {
  if (!value) return '';
  const source = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(source.getTime())) {
    return '';
  }
  const pad = (v) => String(v).padStart(2, '0');
  return `${source.getFullYear()}-${pad(source.getMonth() + 1)}-${pad(source.getDate())}T${pad(
    source.getHours()
  )}:${pad(source.getMinutes())}`;
};

const toUtcIsoString = (value) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
};

const getDirectionLabel = (value) => directionLabels[value] ?? value;

const buildDefaultCallStart = () => formatLocalDateTimeInput(new Date());

const getAudioDurationSeconds = (file) =>
  new Promise((resolve) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);

    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(audio.duration) ? Math.round(audio.duration) : null);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    audio.src = url;
  });

const resolveClientByPhone = (phone, clients) => {
  const normalizedPhone = normalizePhoneValue(phone);
  if (!normalizedPhone || !Array.isArray(clients) || clients.length === 0) {
    return null;
  }

  const matchedClient = clients.find(
    (client) => normalizePhoneValue(client?.phone) === normalizedPhone
  );

  return matchedClient?.id ?? null;
};

const findOrCreateClientByPhone = async (phone, clients, createClientFn) => {
  const normalizedPhone = normalizePhoneValue(phone);
  if (!normalizedPhone) {
    return null;
  }

  const existingClientId = resolveClientByPhone(normalizedPhone, clients);
  if (existingClientId) {
    return existingClientId;
  }

  const createdClient = await createClientFn({
    name: `Клиент ${normalizedPhone}`,
    phone: normalizedPhone
  });

  return createdClient?.id ?? null;
};

const formatParsedFilenameDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return parsed.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const TasksManager = ({ dateRange }) => {
  const { toast } = useToast();
  const { features } = useFeatures();
  const { timeZone } = useTimeZone();
  const transcriptsVisible = features.transcriptsVisible;
  const biometryEnabled = features.biometryEnabled;
  const [tasks, setTasks] = useState([]);
  const [queueSummary, setQueueSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [restartingTaskId, setRestartingTaskId] = useState(null);
  const [reprocessingTaskId, setReprocessingTaskId] = useState(null);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [taskPendingDeletion, setTaskPendingDeletion] = useState(null);
  const [isBulkReprocessing, setIsBulkReprocessing] = useState(false);
  const [bulkRestartDialog, setBulkRestartDialog] = useState(null);
  const [retryingBiometryTaskId, setRetryingBiometryTaskId] = useState(null);
  const [isDownloadingArtifacts, setIsDownloadingArtifacts] = useState(false);
  const [taskTableFilters, setTaskTableFilters] = useState(() => createEmptyTaskTableFilters());
  const [taskTableFilterDraft, setTaskTableFilterDraft] = useState(() => createEmptyTaskTableFilters());

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [callStartedAt, setCallStartedAt] = useState(() => buildDefaultCallStart());
  const [durationMinutes, setDurationMinutes] = useState('');
  const [callDirection, setCallDirection] = useState(directionOptions[0].value);
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [multiFileMetaMode, setMultiFileMetaMode] = useState('common');
  const [parsedFileMeta, setParsedFileMeta] = useState([]);

  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ fullName: '', department: '' });
  const [newClient, setNewClient] = useState({ name: '', company: '', status: 'new', email: '', phone: '' });
  const [isSavingEmployee, setIsSavingEmployee] = useState(false);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [employeeDialogError, setEmployeeDialogError] = useState(null);
  const [clientDialogError, setClientDialogError] = useState(null);

  const [audioPreviewTask, setAudioPreviewTask] = useState(null);
  const [transcriptViewer, setTranscriptViewer] = useState({
    isOpen: false,
    isLoading: false,
    error: null,
    content: '',
    task: null
  });
  const [analysisViewer, setAnalysisViewer] = useState({
    isOpen: false,
    isLoading: false,
    error: null,
    data: null,
    task: null
  });

  const tasksRef = useRef([]);
  const taskDateRangeParams = useMemo(() => buildTaskDateRangeParams(dateRange), [dateRange]);
  const searchScopeKey = useMemo(
    () =>
      JSON.stringify({
        statusFilter,
        taskTableFilters,
        from: taskDateRangeParams.from ?? null,
        to: taskDateRangeParams.to ?? null
      }),
    [statusFilter, taskTableFilters, taskDateRangeParams]
  );
  const previousSearchScopeKeyRef = useRef(searchScopeKey);

  const filteredEmployees = useMemo(() => {
    if (!employeeSearch) {
      return employees;
    }
    const normalized = employeeSearch.trim().toLowerCase();
    return employees.filter((employee) =>
      (employee.fullName ?? '').toLowerCase().includes(normalized)
    );
  }, [employees, employeeSearch]);

  const filteredClients = useMemo(() => {
    if (!clientSearch) {
      return clients;
    }
    const normalized = clientSearch.trim().toLowerCase();
    return clients.filter((client) => {
      const name = client.name ?? client.company ?? '';
      return name.toLowerCase().includes(normalized);
    });
  }, [clients, clientSearch]);

  const createClientDirectoryEntry = useCallback(async (payload) => {
    const created = await createClient(payload);
    setClients((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
    return created;
  }, []);

  const loadEmployeesDirectory = useCallback(async () => {
    setIsLoadingEmployees(true);
    try {
      const list = await fetchEmployees();
      setEmployees(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Не удалось загрузить сотрудников',
        description: 'Попробуйте обновить страницу.',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [toast]);

  const loadClientsDirectory = useCallback(async () => {
    setIsLoadingClients(true);
    try {
      const response = await fetchClients({ limit: 200, offset: 0 });
      setClients(response?.items ?? []);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Не удалось загрузить клиентов',
        description: 'Попробуйте обновить страницу.',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingClients(false);
    }
  }, [toast]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchTasksSummary(taskDateRangeParams);
      setQueueSummary(data ?? null);
    } catch (error) {
      console.error(error);
    }
  }, [taskDateRangeParams]);

  const loadTasks = useCallback(
    async ({ silent = false, pageOverride } = {}) => {
      const targetPage = pageOverride ?? page;
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
        setLoadError(null);
      }

      try {
        const statuses = STATUS_FILTER_MAP[statusFilter] ?? [];
        const requestParams = { page: targetPage, pageSize: PAGE_SIZE, ...taskDateRangeParams };
        if (statuses.length > 0) {
          requestParams.statuses = statuses;
        }
        applyTaskTableFiltersToParams(requestParams, taskTableFilters);

        const response = await fetchTasks(requestParams);
        const items = mapTaskStatuses(response.items ?? []);
        setTasks(items);
        tasksRef.current = items;
        setTotal(response.totalCount ?? response.total ?? items.length);
        setLastUpdatedAt(new Date());
        await loadSummary();
      } catch (error) {
        console.error(error);
        if (!silent) {
          setLoadError('Не удалось загрузить задания. Попробуйте обновить страницу.');
        }
      } finally {
        if (silent) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [page, statusFilter, taskTableFilters, loadSummary, taskDateRangeParams]
  );

  const buildCurrentFilterParams = useCallback(() => {
    const params = { ...taskDateRangeParams };
    const statuses = STATUS_FILTER_MAP[statusFilter] ?? [];
    if (statuses.length > 0) {
      params.statuses = statuses;
    }

    applyTaskTableFiltersToParams(params, taskTableFilters);
    return params;
  }, [statusFilter, taskTableFilters, taskDateRangeParams]);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  useEffect(() => {
    loadEmployeesDirectory();
    loadClientsDirectory();
  }, [loadEmployeesDirectory, loadClientsDirectory]);

  useEffect(() => {
    if (!selectedFiles.length) {
      setParsedFileMeta([]);
      return;
    }

    setParsedFileMeta(
      selectedFiles.map((file) => {
        const parsedMeta = parseCallMetaFromFilename(file.name);
        return {
          fileName: file.name,
          employeeName: parsedMeta?.employeeName ?? '',
          employeeId: matchEmployeeByName(parsedMeta?.employeeName, employees),
          clientPhone: parsedMeta?.clientPhone ?? '',
          clientId: resolveClientByPhone(parsedMeta?.clientPhone, clients),
          callDirection: parsedMeta?.callDirection ?? '',
          callStartedAt: parsedMeta?.callStartedAt ?? ''
        };
      })
    );
  }, [selectedFiles, employees, clients]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    const scopeChanged = previousSearchScopeKeyRef.current !== searchScopeKey;
    previousSearchScopeKeyRef.current = searchScopeKey;

    if (scopeChanged && page !== 1) {
      setPage(1);
      return;
    }

    loadTasks();
  }, [searchScopeKey, page, loadTasks]);

  const refreshTaskStatuses = useCallback(async () => {
    // Спиннер «Обновление статусов...» — только когда опрашиваются строки страницы: сводка
    // перечитывается на каждом тике, и спиннер мигал бы раз в пять секунд.
    const pollsStatuses = selectActiveTasks(tasksRef.current).length > 0;

    try {
      if (pollsStatuses) {
        setIsRefreshing(true);
      }
      await runTaskStatusPoll({
        tasks: tasksRef.current,
        fetchStatus: fetchTaskStatus,
        normalizeStatus: normalizeTaskStatus,
        applyUpdates: (updatesById) => {
          setTasks((prev) => applyStatusUpdates(prev, updatesById));
          tasksRef.current = applyStatusUpdates(tasksRef.current, updatesById);
        },
        reloadTasks: () => loadTasks({ silent: true }),
        loadSummary
      });
      setLastUpdatedAt(new Date());
    } catch (error) {
      console.error(error);
    } finally {
      if (pollsStatuses) {
        setIsRefreshing(false);
      }
    }
  }, [loadTasks, loadSummary]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshTaskStatuses();
    }, STATUS_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refreshTaskStatuses]);

  const handleManualRefresh = () => {
    loadTasks({ silent: true });
  };

  const handleRestartTask = async (task) => {
    if (!task || task.status !== 'Failed') {
      return;
    }

    setRestartingTaskId(task.id);

    try {
      await restartTask(task.id, { fromCheckpoint: true });
      toast({
        title: 'Задание снова в работе',
        description: 'Обработка будет продолжена с последнего этапа.'
      });
      await loadTasks({ silent: true });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Не удалось перезапустить задание',
        description: 'Обновите список и попробуйте снова.',
        variant: 'destructive'
      });
    } finally {
      setRestartingTaskId(null);
    }
  };

  const handleReprocessTask = async (task) => {
    if (!isTaskTerminal(task)) {
      return;
    }

    setReprocessingTaskId(task.id);

    try {
      await restartTask(task.id, { fromCheckpoint: false });
      toast({
        title: 'Переобработка запущена',
        description: 'Пайплайн перезапущен с нуля.'
      });
      await loadTasks({ silent: true });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Не удалось переобработать задание',
        description: 'Полный перезапуск доступен только для завершенных и упавших заданий.',
        variant: 'destructive'
      });
    } finally {
      setReprocessingTaskId(null);
    }
  };

  const handleRetryBiometry = async (task) => {
    if (!canRetryBiometry(task, biometryEnabled)) {
      return;
    }

    setRetryingBiometryTaskId(task.id);

    try {
      await restartTask(task.id, { fromCheckpoint: false, scope: BIOMETRY_RESTART_SCOPE });
      toast({
        title: 'Биометрия запущена заново',
        description: 'Сотрудник будет определён по голосу ещё раз; распознавание и анализ не повторяются.'
      });
      await loadTasks({ silent: true });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Не удалось повторить биометрию',
        description:
          typeof error?.response?.data === 'string' ? error.response.data : 'Обновите список и попробуйте снова.',
        variant: 'destructive'
      });
    } finally {
      setRetryingBiometryTaskId(null);
    }
  };

  const handleDeleteTask = async (task) => {
    if (!task?.id) {
      return;
    }

    setDeletingTaskId(task.id);

    try {
      await deleteTask(task.id);
      setTaskPendingDeletion(null);
      toast({
        title: 'Задание удалено',
        description: 'Связанные данные очищены.'
      });
      await loadTasks({ silent: true });
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      toast({
        title: status === 409 ? 'Задание ещё обрабатывается' : 'Не удалось удалить задание',
        description:
          status === 409
            ? 'Дождитесь завершения текущей обработки и повторите попытку.'
            : 'Обновите список и попробуйте снова.',
        variant: 'destructive'
      });
    } finally {
      setDeletingTaskId(null);
    }
  };

  const handleBulkRestart = async (dialog) => {
    setBulkRestartDialog(null);
    setIsBulkReprocessing(true);

    try {
      const filterParams = buildCurrentFilterParams();
      const result = await bulkRestartTasks(filterParams, {
        fromCheckpoint: Boolean(dialog?.fromCheckpoint),
        scope: dialog?.scope
      });
      toast(describeBulkRestartResult(dialog, result));
      await loadTasks({ silent: true });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Не удалось выполнить операцию',
        description: 'Попробуйте обновить страницу.',
        variant: 'destructive'
      });
    } finally {
      setIsBulkReprocessing(false);
    }
  };

  const handleDownloadArtifacts = async () => {
    setIsDownloadingArtifacts(true);
    try {
      const filterParams = buildCurrentFilterParams();
      const blob = await downloadTasksArtifacts(filterParams);
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis_${timestamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({
        title: 'Архив готов',
        description: 'Скачивание началось.'
      });
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      toast({
        title: 'Не удалось сформировать архив',
        description:
          status === 400
            ? 'Слишком много заданий в выборке или некорректные параметры. Уточните фильтр.'
            : 'Попробуйте повторить операцию позже.',
        variant: 'destructive'
      });
    } finally {
      setIsDownloadingArtifacts(false);
    }
  };

  const handlePageChange = (direction) => {
    if (direction === 0) return;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const nextPage = Math.min(totalPages, Math.max(1, page + direction));
    if (nextPage === page) return;
    setPage(nextPage);
    loadTasks({ pageOverride: nextPage });
  };

  const handleFileChange = (event) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    setSelectedFiles(files);
    setFormError(null);
    setMultiFileMetaMode(files.length > 1 ? 'filename' : 'common');

    const nextParsedFileMeta = files.map((file) => {
      const parsedMeta = parseCallMetaFromFilename(file.name);
      return {
        fileName: file.name,
        employeeName: parsedMeta?.employeeName ?? '',
        employeeId: matchEmployeeByName(parsedMeta?.employeeName, employees),
        clientPhone: parsedMeta?.clientPhone ?? '',
        clientId: resolveClientByPhone(parsedMeta?.clientPhone, clients),
        callDirection: parsedMeta?.callDirection ?? '',
        callStartedAt: parsedMeta?.callStartedAt ?? ''
      };
    });
    setParsedFileMeta(nextParsedFileMeta);

    const parsedMeta = files.length === 1 ? nextParsedFileMeta[0] : null;
    if (parsedMeta?.callStartedAt) {
      setCallStartedAt(parsedMeta.callStartedAt);
    }
    if (parsedMeta?.callDirection) {
      setCallDirection(parsedMeta.callDirection);
    }
    if (parsedMeta?.employeeName || parsedMeta?.clientPhone) {
      setSelectedEmployeeId(parsedMeta.employeeId ?? '');
      setSelectedClientId(parsedMeta.clientId ?? '');
    }
  };

  const handleEmployeeDialogOpenChange = (open) => {
    setIsEmployeeDialogOpen(open);
    if (!open) {
      setNewEmployee({ fullName: '', department: '' });
      setEmployeeDialogError(null);
    }
  };

  const handleClientDialogOpenChange = (open) => {
    setIsClientDialogOpen(open);
    if (!open) {
      setNewClient({ name: '', company: '', status: 'new', email: '', phone: '' });
      setClientDialogError(null);
    }
  };

  const handleSaveEmployee = async () => {
    if (!newEmployee.fullName.trim()) {
      setEmployeeDialogError('Укажите ФИО сотрудника.');
      return;
    }

    setEmployeeDialogError(null);
    setIsSavingEmployee(true);
    try {
      const payload = {
        fullName: newEmployee.fullName.trim(),
        department: newEmployee.department?.trim() || null,
        status: 'active'
      };
      const created = await createEmployee(payload);
      setEmployees((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      setSelectedEmployeeId(created.id);
      setIsEmployeeDialogOpen(false);
      toast({
        title: 'Сотрудник создан',
        description: 'Запись добавлена в справочник.'
      });
    } catch (error) {
      console.error(error);
      setEmployeeDialogError('Не удалось сохранить сотрудника. Повторите попытку.');
    } finally {
      setIsSavingEmployee(false);
    }
  };

  const handleSaveClient = async () => {
    if (!newClient.name.trim() && !newClient.company.trim()) {
      setClientDialogError('Укажите имя или компанию клиента.');
      return;
    }

    setClientDialogError(null);
    setIsSavingClient(true);
    try {
      const payload = {
        name: newClient.name?.trim(),
        company: newClient.company?.trim(),
        status: newClient.status?.trim() || 'new',
        email: newClient.email?.trim(),
        phone: newClient.phone?.trim()
      };
      const created = await createClient(payload);
      setClients((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      setSelectedClientId(created.id);
      setIsClientDialogOpen(false);
      toast({
        title: 'Клиент создан',
        description: 'Карточка клиента добавлена.'
      });
    } catch (error) {
      console.error(error);
      setClientDialogError('Не удалось сохранить клиента. Повторите попытку.');
    } finally {
      setIsSavingClient(false);
    }
  };

  const openAudioDialog = (task) => {
    setAudioPreviewTask(task);
  };

  const closeAudioDialog = () => {
    setAudioPreviewTask(null);
  };

  const handleViewTranscript = async (task) => {
    setTranscriptViewer({
      isOpen: true,
      isLoading: true,
      error: null,
      content: '',
      task
    });

    try {
      const content = await fetchTaskTranscript(task.id);
      setTranscriptViewer((prev) => ({
        ...prev,
        isLoading: false,
        content
      }));
    } catch (error) {
      console.error(error);
      setTranscriptViewer((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Не удалось загрузить транскрипт. Попробуйте позже.'
      }));
    }
  };

  const closeTranscriptViewer = () => {
    setTranscriptViewer({
      isOpen: false,
      isLoading: false,
      error: null,
      content: '',
      task: null
    });
  };

  const handleViewAnalysis = async (task) => {
    setAnalysisViewer({
      isOpen: true,
      isLoading: true,
      error: null,
      data: null,
      task
    });

    try {
      const data = await fetchTaskAnalysis(task.id);
      setAnalysisViewer((prev) => ({
        ...prev,
        isLoading: false,
        data
      }));
    } catch (error) {
      console.error(error);
      setAnalysisViewer((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Не удалось загрузить результаты анализа.'
      }));
    }
  };

  const closeAnalysisViewer = () => {
    setAnalysisViewer({
      isOpen: false,
      isLoading: false,
      error: null,
      data: null,
      task: null
    });
  };

  const resetForm = () => {
    setSelectedFiles([]);
    setSelectedEmployeeId('');
    setSelectedClientId('');
    setCallStartedAt(buildDefaultCallStart());
    setDurationMinutes('');
    setCallDirection(directionOptions[0].value);
    setTopic('');
    setNotes('');
    setFormError(null);
    setFileInputKey((prev) => prev + 1);
    setMultiFileMetaMode('common');
    setParsedFileMeta([]);
  };

  const handleCreateTask = async (event) => {
    event.preventDefault();
    if (!selectedFiles.length) {
      setFormError('Выберите хотя бы один файл для анализа.');
      return;
    }

    const baseCallStartedAtUtc = toUtcIsoString(callStartedAt);
    if (!baseCallStartedAtUtc) {
      setFormError('Укажите корректные дату и время звонка.');
      return;
    }

    const parsedDuration = Number(durationMinutes);
    const durationSeconds =
      Number.isFinite(parsedDuration) && parsedDuration > 0
        ? Math.round(parsedDuration * 60)
        : undefined;

    setIsSubmitting(true);
    setFormError(null);

    try {
      const commonPayload = {
        employeeId: selectedEmployeeId || undefined,
        clientId: selectedClientId || undefined,
        durationSeconds,
        topic: topic.trim(),
        notes: notes.trim()
      };

      if (multiFileMetaMode === 'filename' && selectedFiles.length > 1) {
        const parsedFiles = selectedFiles.map((file) => {
          const parsedMeta = parseCallMetaFromFilename(file.name);
          return {
            file,
            parsedMeta,
            employeeId: matchEmployeeByName(parsedMeta?.employeeName, employees)
          };
        });

        const uniqueClientPhones = [
          ...new Set(
            parsedFiles
              .map((item) => normalizePhoneValue(item.parsedMeta?.clientPhone))
              .filter(Boolean)
          )
        ];
        const clientResults = await Promise.allSettled(
          uniqueClientPhones.map(async (phone) => {
            const clientId = await findOrCreateClientByPhone(
              phone,
              clients,
              createClientDirectoryEntry
            );
            return [phone, clientId];
          })
        );
        const clientIdsByPhone = new Map(
          clientResults
            .filter((result) => result.status === 'fulfilled' && result.value?.[1])
            .map((result) => result.value)
        );

        const durationResults = await Promise.all(
          selectedFiles.map((file) => getAudioDurationSeconds(file))
        );

        const tasks = parsedFiles.map((item, index) => {
          const resolvedCallStartedAt = item.parsedMeta?.callStartedAt ?? callStartedAt;
          const resolvedDirection = item.parsedMeta?.callDirection ?? callDirection;
          const resolvedCallStartedAtUtc =
            toUtcIsoString(resolvedCallStartedAt) ?? baseCallStartedAtUtc;
          const resolvedClientId =
            clientIdsByPhone.get(normalizePhoneValue(item.parsedMeta?.clientPhone)) ??
            commonPayload.clientId;
          const resolvedDurationSeconds = durationResults[index] ?? commonPayload.durationSeconds;

          return createAnalyticalTasks({
            ...commonPayload,
            files: [item.file],
            employeeId: item.employeeId || commonPayload.employeeId,
            clientId: resolvedClientId || undefined,
            callStartedAtUtc: resolvedCallStartedAtUtc,
            durationSeconds: resolvedDurationSeconds ?? undefined,
            callDirection: resolvedDirection
          });
        });

        const results = await Promise.allSettled(tasks);
        const successCount = results.filter((item) => item.status === 'fulfilled').length;
        const failedCount = results.length - successCount;

        if (successCount > 0) {
          toast({
            title: successCount > 1 ? 'Задания созданы' : 'Задание создано',
            description:
              failedCount > 0
                ? `Успешно добавлено: ${successCount}, ошибок: ${failedCount}.`
                : `В очередь добавлено ${successCount} фай${successCount === 1 ? 'л' : 'ла'}.`
          });
        }

        if (failedCount > 0) {
          toast({
            title: 'Часть заданий не создана',
            description: 'Проверьте формат названий файлов и попробуйте снова.',
            variant: 'destructive'
          });
        }
      } else {
        let singleEmployeeId = commonPayload.employeeId;
        let singleClientId = commonPayload.clientId;
        let singleDurationSeconds = commonPayload.durationSeconds;

        if (selectedFiles.length === 1) {
          const parsedMeta = parseCallMetaFromFilename(selectedFiles[0]?.name);
          const hasVirtualPbxMeta = Boolean(parsedMeta?.employeeName || parsedMeta?.clientPhone);

          if (hasVirtualPbxMeta) {
            if (!singleEmployeeId) {
              singleEmployeeId =
                matchEmployeeByName(parsedMeta?.employeeName, employees) || undefined;
            }

            if (!singleClientId && parsedMeta?.clientPhone) {
              singleClientId =
                (await findOrCreateClientByPhone(
                  parsedMeta.clientPhone,
                  clients,
                  createClientDirectoryEntry
                )) || undefined;
            }

            if (singleDurationSeconds == null) {
              singleDurationSeconds = (await getAudioDurationSeconds(selectedFiles[0])) ?? undefined;
            }
          }
        }

        const response = await createAnalyticalTasks({
          files: selectedFiles,
          ...commonPayload,
          employeeId: singleEmployeeId,
          clientId: singleClientId,
          durationSeconds: singleDurationSeconds,
          callStartedAtUtc: baseCallStartedAtUtc,
          callDirection
        });
        const createdCount =
          Array.isArray(response?.ids) && response.ids.length > 0
            ? response.ids.length
            : selectedFiles.length;
        toast({
          title: createdCount > 1 ? 'Задания созданы' : 'Задание создано',
          description:
            createdCount > 1
              ? `В очередь добавлено ${createdCount} файлов.`
              : 'Файл отправлен в очередь на обработку.'
        });
      }

      resetForm();
      setPage(1);
      await loadTasks({ silent: true, pageOverride: 1 });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Не удалось создать задание',
        description: 'Проверьте подключение и повторите попытку.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / PAGE_SIZE)),
    [total]
  );

  const getSummaryCount = useCallback(
    (status) => queueSummary?.statusCounts?.[status] ?? 0,
    [queueSummary]
  );

  const isTaskTableFilterApplied = useMemo(
    () => Object.values(taskTableFilters).some((value) => String(value).trim() !== ''),
    [taskTableFilters]
  );
  const isTaskTableFilterDirty = useMemo(
    () => JSON.stringify(taskTableFilterDraft) !== JSON.stringify(taskTableFilters),
    [taskTableFilterDraft, taskTableFilters]
  );

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('ru-RU', {
        dateStyle: 'short',
        timeStyle: 'medium'
      });
    } catch {
      return value;
    }
  };

  const renderStatusBadge = (task) => {
    const status = task?.status;
    const config = getStatusConfig(status);
    const badgeContent = (
      <Badge className={`${config.badgeClass} font-semibold`}>{config.label}</Badge>
    );

    if (status !== 'Failed' || !task?.errorMessage) {
      return badgeContent;
    }

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">{badgeContent}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-left break-words">
            <p className="font-medium">Ошибка:</p>
            <p>{task.errorMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderAutoRetryNote = (task) => {
    const autoRetry = describeTaskAutoRetry(task, timeZone);
    if (!autoRetry) {
      return null;
    }
    return (
      <div className="flex items-start gap-1 text-xs text-amber-600" title={autoRetry.title} data-auto-retry>
        <Clock3 className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>{autoRetry.label}</span>
      </div>
    );
  };

  const renderWarningNote = (task) => {
    if (!task?.warningMessage) {
      return null;
    }
    return (
      <div className="flex items-start gap-1 text-xs text-amber-600">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span className="break-words">{task.warningMessage}</span>
      </div>
    );
  };

  const renderMetadata = (metadata) => {
    if (!metadata) {
      return <span className="text-sm text-muted-foreground">—</span>;
    }

    const durationLabel = formatDuration(metadata.durationSeconds);
    const talkDurationLabel = formatDuration(metadata.talkTimeSeconds);
    const waitSeconds =
      typeof metadata.durationSeconds === 'number' && typeof metadata.talkTimeSeconds === 'number'
        ? Math.max(0, metadata.durationSeconds - metadata.talkTimeSeconds)
        : null;
    const waitDurationLabel = formatDuration(waitSeconds);

    const biometry = describeTaskBiometry(metadata);

    return (
      <div className="space-y-2 text-xs">
        {metadata.employeeName && (
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{metadata.employeeName}</p>
            {metadata.employeeId && (
              <p className="text-muted-foreground break-words">ID: {metadata.employeeId}</p>
            )}
          </div>
        )}
        {biometry && (
          <p
            className={`flex items-center gap-1 ${BIOMETRY_TONE_CLASSES[biometry.tone]}`}
            title={biometry.title}
          >
            <Fingerprint className="h-3 w-3 shrink-0" />
            {biometry.label}
          </p>
        )}
        {metadata.clientName && (
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{metadata.clientName}</p>
            {metadata.clientId && (
              <p className="text-muted-foreground break-words">ID: {metadata.clientId}</p>
            )}
          </div>
        )}
        {metadata.callStartedAtUtc && (
          <div className="text-muted-foreground">
            <span>Начало: </span>
            <span className="text-foreground">{formatDate(metadata.callStartedAtUtc)}</span>
          </div>
        )}
        {(durationLabel || talkDurationLabel || waitDurationLabel) && (
          <div className="space-y-1 text-muted-foreground">
            {durationLabel && (
              <p>
                Общая длительность: <span className="text-foreground">{durationLabel}</span>
              </p>
            )}
            {talkDurationLabel && (
              <p>
                Время разговора: <span className="text-foreground">{talkDurationLabel}</span>
              </p>
            )}
            {waitDurationLabel && (
              <p>
                Ожидание и удержание: <span className="text-foreground">{waitDurationLabel}</span>
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {metadata.callDirection && (
            <Badge variant="outline">{getDirectionLabel(metadata.callDirection)}</Badge>
          )}
          {metadata.topic && (
            <Badge variant="secondary" className="capitalize">
              {metadata.topic}
            </Badge>
          )}
        </div>
        {metadata.notes && (
          <p className="text-muted-foreground leading-snug line-clamp-3">{metadata.notes}</p>
        )}
      </div>
    );
  };

  const renderResults = (task) => {
    if (!task?.resultsPath) {
      return (
        <span className="text-sm text-muted-foreground">
          Результаты появятся после завершения анализа.
        </span>
      );
    }
    return (
      <div className="space-y-1 text-xs">
        <div className="break-all font-semibold text-gray-900 dark:text-gray-100">{task.resultsPath}</div>
        <p className="text-muted-foreground">
          Используйте действия, чтобы открыть аналитику прямо в интерфейсе.
        </p>
      </div>
    );
  };

  const renderChecklist = (analysis) => {
    if (!analysis) {
      return null;
    }
    const checklistData = analysis.checklist ?? analysis.Checklist;
    if (!checklistData || typeof checklistData !== 'object') {
      return null;
    }

    return (
      <div>
        <p className="text-xs uppercase text-muted-foreground mb-2">Чек-лист</p>
        <div className="space-y-1 rounded-md border p-3 text-xs">
          {Object.entries(checklistData).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <span className="font-medium text-foreground">{key}</span>
              <span className="break-words text-muted-foreground">{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderExtractionVariables = (analysis) => {
    if (!analysis) {
      return null;
    }
    const variables = analysis.promptVariables ?? analysis.PromptVariables;
    if (!variables || typeof variables !== 'object' || Object.keys(variables).length === 0) {
      return null;
    }
    const missing = analysis.missingPromptVariables ?? analysis.MissingPromptVariables;

    return (
      <div className="space-y-2">
        <p className="text-xs uppercase text-muted-foreground">Распарсенные переменные</p>
        <div className="space-y-1 rounded-md border p-3 text-xs">
          {Object.entries(variables).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <span className="font-medium text-foreground">{key}</span>
              <span className="text-muted-foreground text-right break-words">{value ?? '—'}</span>
            </div>
          ))}
        </div>
        {Array.isArray(missing) && missing.length > 0 && (
          <p className="text-xs text-amber-600">
            Нет значений для: {missing.join(', ')}
          </p>
        )}
      </div>
    );
  };

  const handleTaskFilterChange = (field, value) => {
    setTaskTableFilterDraft((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyTaskFilters = () => {
    setPage(1);
    setTaskTableFilters(taskTableFilterDraft);
  };

  const handleResetTaskFilters = () => {
    const emptyFilters = createEmptyTaskTableFilters();
    setPage(1);
    setTaskTableFilterDraft(emptyFilters);
    setTaskTableFilters(emptyFilters);
  };

  return (
    <>
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Импорт звонков</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Управляйте загрузками, отслеживайте статусы и результаты обработки разговоров.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Новое задание
            </CardTitle>
            <CardDescription>
              Загрузите аудио или видео файл и заполните карточку звонка, чтобы аналитика автоматически привязалась к сотруднику и клиенту.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-6" onSubmit={handleCreateTask}>
              <div className="space-y-2">
                <Label htmlFor="task-file">Файлы для анализа</Label>
                <Input
                  key={fileInputKey}
                  id="task-file"
                  type="file"
                  accept="audio/*,video/*,.mp3,.wav,.m4a,.flac,.ogg,.mp4"
                  multiple
                  onChange={handleFileChange}
                  data-ev="tasks.import.pick_files"
                />
                {selectedFiles.length > 0 && (
                  <div className="text-sm text-muted-foreground space-y-1 max-h-28 overflow-y-auto pr-2">
                    <p>
                      Выбрано файлов: <span className="font-semibold text-foreground">{selectedFiles.length}</span>
                    </p>
                    <ul className="space-y-1">
                      {selectedFiles.map((file, index) => (
                        <li key={`${file.name}-${file.size}-${index}`} className="flex flex-wrap items-center gap-2">
                          <span className="break-all font-medium text-foreground">{file.name}</span>
                          <span>({(file.size / 1024 / 1024).toFixed(2)} МБ)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedFiles.length > 1 && (
                  <div className="space-y-2 rounded-md border border-dashed p-3">
                    <Label className="text-sm font-medium">Заполнение данных</Label>
                    <RadioGroup
                      trackId="tasks.import.meta_mode"
                      trackValue
                      value={multiFileMetaMode}
                      onValueChange={setMultiFileMetaMode}
                      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                    >
                      <div
                        className={`flex flex-col gap-1 rounded-md border p-3 ${
                          multiFileMetaMode === 'common'
                            ? 'border-primary bg-primary/5'
                            : 'border-border'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="common" id="multi-mode-common" />
                          <Label htmlFor="multi-mode-common" className="cursor-pointer font-medium">
                            Один набор для всех файлов
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Использовать значения формы (дата/время, направление) для каждого файла.
                        </p>
                      </div>
                      <div
                        className={`flex flex-col gap-1 rounded-md border p-3 ${
                          multiFileMetaMode === 'filename'
                            ? 'border-primary bg-primary/5'
                            : 'border-border'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="filename" id="multi-mode-filename" />
                          <Label htmlFor="multi-mode-filename" className="cursor-pointer font-medium">
                            Считать из названий файлов
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Дата/время, направление, имя сотрудника и телефон клиента считываются из
                          имени файла формата{' '}
                          <code className="break-all">ГГГГ-ММ-ДД__чч-мм-сс__Сотрудник__Телефон</code> или{' '}
                          <code className="break-all">ГГГГ-ММ-ДД__чч-мм-сс__Телефон__Сотрудник</code>.
                        </p>
                      </div>
                    </RadioGroup>
                  </div>
                )}
                {multiFileMetaMode === 'filename' && parsedFileMeta.length >= 2 && (
                  <div className="rounded-md border">
                    <div className="border-b px-3 py-2">
                      <p className="text-sm font-medium">Предпросмотр распознанных данных</p>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Файл</TableHead>
                            <TableHead>Сотрудник</TableHead>
                            <TableHead>Телефон</TableHead>
                            <TableHead>Направление</TableHead>
                            <TableHead>Дата/время</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parsedFileMeta.map((item, index) => {
                            const isParsed = Boolean(
                              item.callStartedAt ||
                                item.callDirection ||
                                item.employeeName ||
                                item.clientPhone
                            );

                            return (
                              <TableRow key={`${item.fileName}-${index}`}>
                                <TableCell className="max-w-xs break-all font-medium">
                                  {item.fileName}
                                </TableCell>
                                <TableCell>
                                  {item.employeeName ? (
                                    item.employeeId ? (
                                      <span className="text-emerald-700">{item.employeeName}</span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-amber-600">
                                        <AlertTriangle className="h-4 w-4" />
                                        {item.employeeName}
                                      </span>
                                    )
                                  ) : isParsed ? (
                                    '—'
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-amber-600">
                                      <AlertTriangle className="h-4 w-4" />
                                      не найден
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell>{item.clientPhone || '—'}</TableCell>
                                <TableCell>
                                  {item.callDirection ? getDirectionLabel(item.callDirection) : '—'}
                                </TableCell>
                                <TableCell>
                                  {item.callStartedAt
                                    ? formatParsedFilenameDateTime(item.callStartedAt)
                                    : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label htmlFor="employee-select">Сотрудник</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={loadEmployeesDirectory}
                        data-ev="tasks.import.employee_refresh"
                        disabled={isLoadingEmployees}
                      >
                        <RefreshCw className={`h-4 w-4 ${isLoadingEmployees ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!selectedEmployeeId}
                        onClick={() => setSelectedEmployeeId('')}
                        data-ev="tasks.import.employee_clear"
                      >
                        Очистить
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEmployeeDialogOpen(true)}
                        data-ev="tasks.import.employee_new"
                      >
                        Новый
                      </Button>
                    </div>
                  </div>
                  <Select trackId="tasks.import.employee" value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                    <SelectTrigger id="employee-select">
                      <SelectValue
                        placeholder={isLoadingEmployees ? 'Загрузка списка...' : 'Выберите сотрудника'}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredEmployees.length === 0 ? (
                        <SelectItem value="__employee-empty" disabled>
                          {isLoadingEmployees ? 'Загрузка...' : 'Совпадений нет'}
                        </SelectItem>
                      ) : (
                        filteredEmployees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.fullName ?? 'Без имени'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Поиск по имени"
                    value={employeeSearch}
                    onChange={(event) => setEmployeeSearch(event.target.value)}
                    data-ev="tasks.import.employee_search"
                  />
                  <p className="text-xs text-muted-foreground">
                    Можно оставить поле пустым, если звонок не закреплен за сотрудником.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label htmlFor="client-select">Клиент</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={loadClientsDirectory}
                        data-ev="tasks.import.client_refresh"
                        disabled={isLoadingClients}
                      >
                        <RefreshCw className={`h-4 w-4 ${isLoadingClients ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!selectedClientId}
                        onClick={() => setSelectedClientId('')}
                        data-ev="tasks.import.client_clear"
                      >
                        Очистить
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsClientDialogOpen(true)}
                        data-ev="tasks.import.client_new"
                      >
                        Новый
                      </Button>
                    </div>
                  </div>
                  <Select trackId="tasks.import.client" value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger id="client-select">
                      <SelectValue
                        placeholder={isLoadingClients ? 'Загрузка списка...' : 'Выберите клиента'}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredClients.length === 0 ? (
                        <SelectItem value="__client-empty" disabled>
                          {isLoadingClients ? 'Загрузка...' : 'Совпадений нет'}
                        </SelectItem>
                      ) : (
                        filteredClients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name ?? client.company ?? 'Без названия'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Поиск по имени или компании"
                    value={clientSearch}
                    onChange={(event) => setClientSearch(event.target.value)}
                    data-ev="tasks.import.client_search"
                  />
                  <p className="text-xs text-muted-foreground">
                    Можно добавить клиента позже — аналитика подтянется по идентификатору.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="call-started-at">Дата и время звонка</Label>
                  <Input
                    id="call-started-at"
                    type="datetime-local"
                    value={callStartedAt}
                    onChange={(event) => setCallStartedAt(event.target.value)}
                    data-ev="tasks.import.call_started_at"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="call-duration">Длительность, мин</Label>
                  <Input
                    id="call-duration"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.5"
                    value={durationMinutes}
                    onChange={(event) => setDurationMinutes(event.target.value)}
                    data-ev="tasks.import.duration"
                    placeholder="9.5"
                  />
                  <p className="text-xs text-muted-foreground">
                    Можно оставить пустым, если длительность неизвестна.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Направление звонка</Label>
                <RadioGroup
                  trackId="tasks.import.direction"
                  trackValue
                  value={callDirection}
                  onValueChange={setCallDirection}
                  className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                >
                  {directionOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`flex items-center gap-2 rounded-md border p-3 ${
                        callDirection === option.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <RadioGroupItem
                        value={option.value}
                        id={`call-direction-${option.value}`}
                        className="mr-2"
                      />
                      <Label
                        htmlFor={`call-direction-${option.value}`}
                        className="flex-1 cursor-pointer"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="call-topic">Тема звонка</Label>
                  <Input
                    id="call-topic"
                    value={topic}
                    onChange={(event) => setTopic(event.target.value)}
                    data-ev="tasks.import.topic"
                    placeholder="Например, презентация продукта"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="call-notes">Заметки для аналитики</Label>
                  <Textarea
                    id="call-notes"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    data-ev="tasks.import.notes"
                    rows={4}
                    placeholder="Опишите цель звонка, ожидания клиента или важные ссылки."
                  />
                </div>
              </div>

              {formError && (
                <p className="text-sm text-red-600">{formError}</p>
              )}

              <div className="flex flex-wrap items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetForm}
                  data-ev="tasks.import.reset"
                  disabled={isSubmitting}
                >
                  Сбросить
                </Button>
                <Button type="submit" disabled={isSubmitting || selectedFiles.length === 0} data-ev="tasks.import.submit">
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Отправить в обработку
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Состояние очереди
            </CardTitle>
            <CardDescription>
              Данные обновляются автоматически каждые 5 секунд
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">В работе</p>
                <p className="text-2xl font-semibold">{queueSummary?.totalActive ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Готово</p>
                <p className="text-2xl font-semibold">{queueSummary?.totalCompleted ?? 0}</p>
              </div>
            </div>

            <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs text-muted-foreground">Успешно обработано за интервал</p>
              <p className="text-2xl font-semibold">
                {formatProcessedMinutes(queueSummary?.totalCompletedMinutes)}
              </p>
              <p className="text-xs text-muted-foreground">
                Ориентировочная стоимость: {formatEstimatedCost(queueSummary?.estimatedCompletedCostRubles)}
              </p>
            </div>

            <div className="space-y-3">
              {['NotStarted', 'RecognitionRunning', 'RecognitionResultsSaved', 'AnalysisCompleted', 'Completed', 'Failed', 'Skipped'].map((status) => {
                const config = statusConfig[status] ?? statusConfig.default;
                return (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${config.dotClass}`} />
                      <span>{config.label}</span>
                    </div>
                    <span className="font-semibold">{getSummaryCount(status)}</span>
                  </div>
                );
              })}
            </div>

            <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Clock3 className="h-4 w-4" />
                  Последняя проверка
                </span>
                <span>{lastUpdatedAt ? formatDate(lastUpdatedAt) : '—'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>Задания</CardTitle>
            <CardDescription>
              Показаны последние загруженные задания. Всего записей: {total}.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:flex-nowrap">
            {isRefreshing && (
              <span className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Обновление статусов...
              </span>
            )}
            <Select
              trackId="tasks.list.status_filter"
              trackValue
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-48 max-sm:min-w-0 max-sm:flex-1">
                <SelectValue placeholder="Все задания" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все задания</SelectItem>
                <SelectItem value="active">Незавершённые</SelectItem>
                <SelectItem value="failed">Только ошибки</SelectItem>
                <SelectItem value="completed">Завершённые</SelectItem>
                <SelectItem value="skipped">Пропущенные</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleManualRefresh} data-ev="tasks.list.refresh">
              <RefreshCw className="mr-2 h-4 w-4" />
              Обновить
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 space-y-3 rounded-md border border-dashed p-3">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="task-filter-id" className="text-xs text-muted-foreground">
                  Идентификатор
                </Label>
                <Input
                  id="task-filter-id"
                  value={taskTableFilterDraft.id}
                  onChange={(event) => handleTaskFilterChange('id', event.target.value)}
                  data-ev="tasks.filter.id"
                  placeholder="Поиск по ID"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-filter-status" className="text-xs text-muted-foreground">
                  Статус
                </Label>
                <Input
                  id="task-filter-status"
                  value={taskTableFilterDraft.status}
                  onChange={(event) => handleTaskFilterChange('status', event.target.value)}
                  data-ev="tasks.filter.status"
                  placeholder="Ошибка, готово, ..."
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-filter-created-at" className="text-xs text-muted-foreground">
                  Создано
                </Label>
                <Input
                  id="task-filter-created-at"
                  value={taskTableFilterDraft.createdAt}
                  onChange={(event) => handleTaskFilterChange('createdAt', event.target.value)}
                  data-ev="tasks.filter.created_at"
                  placeholder="дата или время"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-filter-updated-at" className="text-xs text-muted-foreground">
                  Обновлено
                </Label>
                <Input
                  id="task-filter-updated-at"
                  value={taskTableFilterDraft.updatedAt}
                  onChange={(event) => handleTaskFilterChange('updatedAt', event.target.value)}
                  data-ev="tasks.filter.updated_at"
                  placeholder="дата или время"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-filter-source" className="text-xs text-muted-foreground">
                  Источник
                </Label>
                <Input
                  id="task-filter-source"
                  value={taskTableFilterDraft.source}
                  onChange={(event) => handleTaskFilterChange('source', event.target.value)}
                  data-ev="tasks.filter.source"
                  placeholder="имя файла или путь"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-filter-metadata" className="text-xs text-muted-foreground">
                  Метаданные
                </Label>
                <Input
                  id="task-filter-metadata"
                  value={taskTableFilterDraft.metadata}
                  onChange={(event) => handleTaskFilterChange('metadata', event.target.value)}
                  data-ev="tasks.filter.metadata"
                  placeholder="сотрудник, клиент, тема..."
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-filter-results" className="text-xs text-muted-foreground">
                  Результат
                </Label>
                <Input
                  id="task-filter-results"
                  value={taskTableFilterDraft.results}
                  onChange={(event) => handleTaskFilterChange('results', event.target.value)}
                  data-ev="tasks.filter.results"
                  placeholder="путь к результатам"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-filter-analysis-content" className="text-xs text-muted-foreground">
                  Текст в результатах анализа
                </Label>
                <Input
                  id="task-filter-analysis-content"
                  value={taskTableFilterDraft.analysisContent}
                  onChange={(event) => handleTaskFilterChange('analysisContent', event.target.value)}
                  data-ev="tasks.filter.analysis_content"
                  placeholder="regex или подстрока"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-0">
              <p className="text-xs text-muted-foreground">
                Поиск по прямому вхождению строки. Для поля по содержимому анализов поиск медленнее. Найдено на странице: {tasks.length}
              </p>
              <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
                <Button
                  type="button"
                  size="sm"
                  disabled={!isTaskTableFilterDirty || isLoading || isRefreshing}
                  onClick={handleApplyTaskFilters}
                  data-ev="tasks.filter.apply"
                >
                  Найти
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isBulkReprocessing}
                  onClick={() => setBulkRestartDialog({ fromCheckpoint: false })}
                  data-ev="tasks.bulk.reprocess_all"
                >
                  {isBulkReprocessing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ListRestart className="mr-2 h-4 w-4" />
                  )}
                  Переобработать все
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isBulkReprocessing}
                  onClick={() => setBulkRestartDialog({ fromCheckpoint: true })}
                  data-ev="tasks.bulk.restart_checkpoint"
                >
                  {isBulkReprocessing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ListRestart className="mr-2 h-4 w-4" />
                  )}
                  Перезапустить с чекпоинта
                </Button>
                {biometryEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isBulkReprocessing}
                    onClick={() => setBulkRestartDialog({ scope: BIOMETRY_RESTART_SCOPE })}
                    data-ev="tasks.bulk.retry_biometry"
                  >
                    {isBulkReprocessing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Fingerprint className="mr-2 h-4 w-4" />
                    )}
                    Повторить биометрию
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isDownloadingArtifacts || isLoading || isRefreshing}
                  onClick={handleDownloadArtifacts}
                  data-ev="tasks.bulk.download_results"
                  title="Скачать ZIP с транскриптами и аналитикой по всем заданиям из выборки"
                >
                  {isDownloadingArtifacts ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Подготовка архива…
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Скачать результаты
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!isTaskTableFilterApplied && !isTaskTableFilterDirty}
                  onClick={handleResetTaskFilters}
                  data-ev="tasks.filter.reset"
                >
                  Сбросить фильтры
                </Button>
              </div>
            </div>
          </div>

          {loadError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Загрузка данных...
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {isTaskTableFilterApplied ? (
                <>
                  <ClipboardList className="mx-auto mb-4 h-10 w-10 text-gray-300" />
                  Нет совпадений по текущим фильтрам.
                </>
              ) : (
                <>
                  <FileText className="mx-auto mb-4 h-10 w-10 text-gray-300" />
                  Пока нет заданий. Загрузите файл, чтобы начать анализ.
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">Идентификатор</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Создано</TableHead>
                    <TableHead className="max-md:hidden">Обновлено</TableHead>
                    <TableHead>Источник</TableHead>
                    <TableHead>Метаданные</TableHead>
                    <TableHead className="max-md:hidden">Результат</TableHead>
                    <TableHead className="w-64">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => {
                    const fileName = task.filePath?.split('/')?.pop() ?? task.filePath ?? '—';
                    const canReprocess = isTaskTerminal(task);
                    const isRestartingFromCheckpoint = restartingTaskId === task.id;
                    const isReprocessing = reprocessingTaskId === task.id;
                    const isDeleting = deletingTaskId === task.id;
                    const isRetryingBiometry = retryingBiometryTaskId === task.id;
                    const isTaskActionPending =
                      isRestartingFromCheckpoint || isReprocessing || isDeleting || isRetryingBiometry;
                    return (
                      <TableRow key={task.id}>
                        <TableCell>
                          <p className="font-mono text-xs break-words">{task.id}</p>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {renderStatusBadge(task)}
                            {renderAutoRetryNote(task)}
                            {renderWarningNote(task)}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(task.createdAt)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-md:hidden">{formatDate(task.updatedAt)}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="break-all font-semibold text-gray-900 dark:text-gray-100">{fileName}</p>
                            <p className="text-muted-foreground text-xs break-words">{task.filePath}</p>
                          </div>
                        </TableCell>
                        <TableCell>{renderMetadata(task.metadata)}</TableCell>
                        <TableCell className="max-md:hidden">{renderResults(task)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!canReprocess || isTaskActionPending}
                              onClick={() => handleReprocessTask(task)}
                              data-ev="tasks.row.reprocess"
                            >
                              {isReprocessing ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="mr-2 h-4 w-4" />
                              )}
                              Переобработать
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openAudioDialog(task)}
                              data-ev="tasks.row.listen"
                            >
                              <PlayCircle className="mr-2 h-4 w-4" />
                              Слушать
                            </Button>
                            {/* Ссылка, а не fetch в blob: запись часового звонка браузер пишет на диск
                                потоком, а не держит целиком в памяти вкладки. */}
                            <Button asChild variant="outline" size="sm" data-ev="tasks.row.download_audio">
                              <a
                                href={getTaskAudioUrl(task.id, { download: true })}
                                download={taskAudioFileName(task.filePath) ?? ''}
                                data-ev="tasks.row.download_audio"
                              >
                                <Download className="mr-2 h-4 w-4" />
                                Скачать
                              </a>
                            </Button>
                            {transcriptsVisible && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={!isTranscriptAvailable(task)}
                                onClick={() => handleViewTranscript(task)}
                                data-ev="tasks.row.transcript"
                              >
                                <ScrollText className="mr-2 h-4 w-4" />
                                Транскрипт
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!isAnalysisAvailable(task)}
                              onClick={() => handleViewAnalysis(task)}
                              data-ev="tasks.row.analysis"
                            >
                              <Sparkles className="mr-2 h-4 w-4" />
                              Аналитика
                            </Button>
                            {task.status === 'Failed' && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={isTaskActionPending}
                                onClick={() => handleRestartTask(task)}
                                data-ev="tasks.row.restart_checkpoint"
                              >
                                {isRestartingFromCheckpoint ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="mr-2 h-4 w-4" />
                                )}
                                Перезапустить с чекпоинта
                              </Button>
                            )}
                            {canRetryBiometry(task, biometryEnabled) && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={isTaskActionPending}
                                onClick={() => handleRetryBiometry(task)}
                                data-ev="tasks.row.retry_biometry"
                              >
                                {isRetryingBiometry ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Fingerprint className="mr-2 h-4 w-4" />
                                )}
                                Повторить биометрию
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isTaskActionPending}
                              onClick={() => setTaskPendingDeletion(task)}
                              data-ev="tasks.row.delete"
                            >
                              {isDeleting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="mr-2 h-4 w-4 text-red-500" />
                              )}
                              Удалить
                            </Button>
                          </div>
                          {transcriptsVisible && !isTranscriptAvailable(task) && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Транскрипт появится после этапа «{statusConfig.RecognitionResultsSaved.label}».
                            </p>
                          )}
                          {!isAnalysisAvailable(task) && (
                            <p className="text-xs text-muted-foreground">
                              Аналитика доступна после завершения обработки.
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Страница {page} из {totalPages}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePageChange(-1)} disabled={page === 1} data-ev="tasks.list.prev_page">
                Назад
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                data-ev="tasks.list.next_page"
                disabled={page === totalPages}
              >
                Вперед
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

      <AlertDialog
        open={bulkRestartDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBulkRestartDialog(null);
          }
        }}
      >
        <AlertDialogContent trackId="tasks.bulk_restart">
          <AlertDialogHeader>
            <AlertDialogTitle>{describeBulkRestart(bulkRestartDialog).title}</AlertDialogTitle>
            <AlertDialogDescription>{describeBulkRestart(bulkRestartDialog).description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-ev="tasks.bulk_restart.cancel">Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={isBulkReprocessing}
              onClick={() => handleBulkRestart(bulkRestartDialog)}
              data-ev="tasks.bulk_restart.confirm"
            >
              Запустить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(taskPendingDeletion)}
        onOpenChange={(open) => {
          if (!open && !deletingTaskId) {
            setTaskPendingDeletion(null);
          }
        }}
      >
        <AlertDialogContent trackId="tasks.delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить задание?</AlertDialogTitle>
            <AlertDialogDescription>
              Будут удалены само задание, связанные файлы и аналитика. Остальные задания и их данные должны остаться без изменений.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingTaskId)} data-ev="tasks.delete.cancel">Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={!taskPendingDeletion || Boolean(deletingTaskId)}
              onClick={() => handleDeleteTask(taskPendingDeletion)}
              data-ev="tasks.delete.confirm"
            >
              {deletingTaskId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Удаление...
                </>
              ) : (
                'Удалить'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isEmployeeDialogOpen} onOpenChange={handleEmployeeDialogOpenChange}>
        <DialogContent trackId="tasks.new_employee">
          <DialogHeader>
            <DialogTitle>Новый сотрудник</DialogTitle>
            <DialogDescription>Заполните основные поля, чтобы привязать звонок к сотруднику.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-employee-name">ФИО</Label>
              <Input
                id="new-employee-name"
                value={newEmployee.fullName}
                onChange={(event) =>
                  setNewEmployee((prev) => ({ ...prev, fullName: event.target.value }))
                }
                data-ev="tasks.new_employee.full_name"
                placeholder="Иван Иванов"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-employee-department">Отдел</Label>
              <Input
                id="new-employee-department"
                value={newEmployee.department}
                onChange={(event) =>
                  setNewEmployee((prev) => ({ ...prev, department: event.target.value }))
                }
                data-ev="tasks.new_employee.department"
                placeholder="Продажи"
              />
            </div>
            {employeeDialogError && <p className="text-sm text-red-600">{employeeDialogError}</p>}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleEmployeeDialogOpenChange(false)}
                data-ev="tasks.new_employee.cancel"
                disabled={isSavingEmployee}
              >
                Отмена
              </Button>
              <Button type="button" onClick={handleSaveEmployee} disabled={isSavingEmployee} data-ev="tasks.new_employee.save">
                {isSavingEmployee && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Сохранить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isClientDialogOpen} onOpenChange={handleClientDialogOpenChange}>
        <DialogContent trackId="tasks.new_client">
          <DialogHeader>
            <DialogTitle>Новый клиент</DialogTitle>
            <DialogDescription>Создайте карточку клиента, чтобы быстрее находить звонки.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-client-name">Имя</Label>
              <Input
                id="new-client-name"
                value={newClient.name}
                onChange={(event) => setNewClient((prev) => ({ ...prev, name: event.target.value }))}
                data-ev="tasks.new_client.name"
                placeholder="Мария Петрова"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-client-company">Компания</Label>
              <Input
                id="new-client-company"
                value={newClient.company}
                onChange={(event) =>
                  setNewClient((prev) => ({ ...prev, company: event.target.value }))
                }
                data-ev="tasks.new_client.company"
                placeholder="ООО «Пример»"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-client-status">Статус</Label>
              <Input
                id="new-client-status"
                value={newClient.status}
                onChange={(event) =>
                  setNewClient((prev) => ({ ...prev, status: event.target.value }))
                }
                data-ev="tasks.new_client.status"
                placeholder="new / active / in-progress"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-client-email">Email</Label>
              <Input
                id="new-client-email"
                type="email"
                value={newClient.email}
                onChange={(event) => setNewClient((prev) => ({ ...prev, email: event.target.value }))}
                data-ev="tasks.new_client.email"
                placeholder="client@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-client-phone">Телефон</Label>
              <Input
                id="new-client-phone"
                value={newClient.phone}
                onChange={(event) => setNewClient((prev) => ({ ...prev, phone: event.target.value }))}
                data-ev="tasks.new_client.phone"
                placeholder="+7 (900) 000-00-00"
              />
            </div>
            {clientDialogError && <p className="text-sm text-red-600">{clientDialogError}</p>}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleClientDialogOpenChange(false)}
                data-ev="tasks.new_client.cancel"
                disabled={isSavingClient}
              >
                Отмена
              </Button>
              <Button type="button" onClick={handleSaveClient} disabled={isSavingClient} data-ev="tasks.new_client.save">
                {isSavingClient && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Сохранить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(audioPreviewTask)}
        onOpenChange={(open) => {
          if (!open) {
            closeAudioDialog();
          }
        }}
      >
        <DialogContent trackId="tasks.audio_preview" className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Прослушивание</DialogTitle>
            <DialogDescription>
              Управляйте воспроизведением, чтобы проверить качество исходного файла.
            </DialogDescription>
          </DialogHeader>
          {audioPreviewTask && (
            <div className="space-y-4">
              <div>
                <p className="break-all text-sm font-medium text-foreground">
                  {audioPreviewTask.filePath?.split('/')?.pop() ?? 'Файл'}
                </p>
                <p className="text-xs text-muted-foreground break-all">{audioPreviewTask.filePath}</p>
              </div>
              <audio
                className="w-full"
                src={getTaskAudioUrl(audioPreviewTask.id)}
                controls
                controlsList="nodownload"
                data-ev="tasks.audio_preview.player"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={transcriptViewer.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeTranscriptViewer();
          }
        }}
      >
        <DialogContent trackId="tasks.transcript_viewer" className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Транскрипт</DialogTitle>
            {transcriptViewer.task && (
              <DialogDescription className="break-words">
                Задание: {transcriptViewer.task.id}
              </DialogDescription>
            )}
          </DialogHeader>
          {transcriptViewer.isLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Загрузка...
            </div>
          ) : transcriptViewer.error ? (
            <p className="text-sm text-red-600">{transcriptViewer.error}</p>
          ) : (
            <ScrollArea className="h-80 rounded-md border border-dashed p-4">
              <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                {transcriptViewer.content || 'Пустой файл'}
              </pre>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={analysisViewer.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeAnalysisViewer();
          }
        }}
      >
        <DialogContent trackId="tasks.analysis_viewer" className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Результаты анализа</DialogTitle>
            {analysisViewer.task && (
              <DialogDescription className="break-words">
                Задание: {analysisViewer.task.id}
              </DialogDescription>
            )}
          </DialogHeader>
          {analysisViewer.isLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Загрузка...
            </div>
          ) : analysisViewer.error ? (
            <p className="text-sm text-red-600">{analysisViewer.error}</p>
          ) : analysisViewer.data ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {analysisViewer.data.topic && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Тема</p>
                    <p className="font-medium text-foreground">{analysisViewer.data.topic}</p>
                  </div>
                )}
                {analysisViewer.data.resultType && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Результат</p>
                    <p className="font-medium text-foreground">{analysisViewer.data.resultType}</p>
                  </div>
                )}
                {analysisViewer.data.checklistScore && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Оценка чек-листа</p>
                    <p className="font-medium text-foreground">{analysisViewer.data.checklistScore}</p>
                  </div>
                )}
                {analysisViewer.data.qualityScore && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Итоговое качество</p>
                    <p className="font-medium text-foreground">{analysisViewer.data.qualityScore}</p>
                  </div>
                )}
              </div>
              {analysisViewer.data.improvement && (
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Рекомендации</p>
                  <p className="text-sm leading-snug">{analysisViewer.data.improvement}</p>
                </div>
              )}
              {renderExtractionVariables(analysisViewer.data)}
              {renderChecklist(analysisViewer.data)}
              <div>
                <p className="text-xs uppercase text-muted-foreground mb-2">Полные данные</p>
                <ScrollArea className="h-64 rounded-md border border-dashed p-4">
                  <pre className="text-xs whitespace-pre-wrap break-words">
                    {JSON.stringify(analysisViewer.data, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TasksManager;
